import React, { useEffect, useMemo, useRef } from 'react';
import { usePageContext } from 'react-pdf';
import makeCancellable from 'make-cancellable-promise';
import * as pdfjs from 'pdfjs-dist';
import invariant from 'tiny-invariant';

const TestRenderer = () => {
  const pageContext = usePageContext();

  invariant(pageContext, 'Unable to find Page context.');

  const { _className, page, rotate, scale } = pageContext;

  invariant(page, 'Attempted to render page SVG, but no page was specified.');

  const svgRef = useRef<SVGElement | null>(null);

  const viewport = useMemo(() => page.getViewport({ scale, rotation: rotate }), [
    page,
    rotate,
    scale,
  ]);

  function renderSVG() {
    if (!page) {
      return;
    }

    const cancellable = makeCancellable(page.getOperatorList());

    const runningTask = cancellable;

    cancellable.promise
      .then(operatorList => {
        const isSomeImage = operatorList.fnArray.some(
          fn => fn === pdfjs.OPS.paintImageXObject,
        );

        if (isSomeImage) {
          runningTask.cancel();
          return;
        }

        const svgGfx = new pdfjs.SVGGraphics(page.commonObjs, page.objs);

        svgGfx.getSVG(operatorList, viewport).then((nextSvg: unknown) => {
          if (!(nextSvg instanceof SVGElement)) {
            throw new Error('Something with PDF rendering. Please contact support.');
          }

          svgRef.current = nextSvg;
        });
      })
      .catch(error => {
        throw new Error(error);
      });

    return () => {
      runningTask.cancel();
    };
  }

  useEffect(renderSVG, [page, viewport, scale, svgRef]);

  function drawPageOnContainer(element: HTMLDivElement | null) {
    const { current: svg } = svgRef;

    if (!element || !svg) {
      return;
    }

    if (!element.firstElementChild) {
      element.appendChild(svg);
    }

    svg.setAttribute('width', `${viewport.width}`);
    svg.setAttribute('height', `${viewport.height}`);
  }

  return (
    <div
      className={`${_className}__svg`}
      // Note: This cannot be shortened, as we need this function to be called with each render.
      ref={ref => drawPageOnContainer(ref)}
      style={{
        display: 'block',
        backgroundColor: 'white',
        overflow: 'hidden',
        width: viewport.width,
        height: viewport.height,
        userSelect: 'none',
      }}
    />
  );
};

export default TestRenderer;
